import React from 'react';
import { colors, spacing, borderRadius, shadows } from '../../styles/designTokens';

/**
 * Card Component
 * 
 * Standardized container component for consistent card layouts.
 * Replaces ad-hoc div containers throughout the application.
 * 
 * Usage:
 * <Card>
 *   <Card.Header>Title</Card.Header>
 *   <Card.Body>Content</Card.Body>
 *   <Card.Footer>Actions</Card.Footer>
 * </Card>
 * 
 * Props:
 * - padding: 'none' | 'sm' | 'md' | 'lg' (default: 'md')
 * - hover: Boolean - adds hover effect
 * - className: Additional CSS classes
 */

const Card = ({
  padding = 'md',
  hover = false,
  className = '',
  children,
  ...props
}) => {
  const paddingStyles = {
    none: '0',
    sm: spacing.sm,
    md: spacing.md,
    lg: spacing.lg
  };

  const cardStyles = {
    backgroundColor: '#ffffff',
    border: `1px solid ${colors.neutral[200]}`,
    borderRadius: borderRadius.md,
    boxShadow: shadows.sm,
    padding: paddingStyles[padding],
    transition: 'all 0.2s ease'
  };

  const hoverStyles = hover ? {
    cursor: 'pointer',
    ':hover': {
      boxShadow: shadows.md,
      transform: 'translateY(-2px)'
    }
  } : {};

  return (
    <div
      style={{ ...cardStyles, ...hoverStyles }}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Card.Header Component
 * 
 * Header section for Card with consistent styling
 */
const CardHeader = ({ className = '', children, ...props }) => {
  return (
    <div
      style={{
        borderBottom: `1px solid ${colors.neutral[200]}`,
        marginBottom: spacing.md,
        paddingBottom: spacing.sm
      }}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
};
CardHeader.displayName = 'Card.Header';
Card.Header = CardHeader;

/**
 * Card.Body Component
 * 
 * Main content section for Card
 */
const CardBody = ({ className = '', children, ...props }) => {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
};
CardBody.displayName = 'Card.Body';
Card.Body = CardBody;

/**
 * Card.Footer Component
 * 
 * Footer section for Card with consistent styling
 */
const CardFooter = ({ className = '', children, ...props }) => {
  return (
    <div
      style={{
        borderTop: `1px solid ${colors.neutral[200]}`,
        marginTop: spacing.md,
        paddingTop: spacing.sm
      }}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
};
CardFooter.displayName = 'Card.Footer';
Card.Footer = CardFooter;

export default Card;

